import { useEffect, useState } from "react";
import EventCard from "./EventCard";
import { DateTime } from "luxon";
import { getNextOccurrence } from "../utils/time"; // adjust path to match your project
import { formatEventCost } from "../utils/eventCost";

function EventsSection({ maxEvents = 0 }) {
    const [events, setEvents] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(true);
    const [eventsError, setEventsError] = useState("");

    useEffect(() => {
        let cancelled = false;

        async function loadEvents() {
            try {
                setLoadingEvents(true);
                setEventsError("");

                const res = await fetch("/.netlify/functions/get-events", {
                    method: "GET",
                    // If you ever need cookies-based auth:
                    // credentials: "include",
                });

                const data = await res.json().catch(() => ({}));

                if (!res.ok || !data.ok) {
                    if (!cancelled) {
                        setEventsError(data.error || "Failed to load events");
                        setLoadingEvents(false);
                    }
                    return;
                }

                if (!cancelled) {
                    setEvents(Array.isArray(data.events) ? data.events : []);
                    setLoadingEvents(false);
                }
            } catch (err) {
                console.error("loadEvents error:", err);
                if (!cancelled) {
                    setEventsError("Network error while loading events");
                    setLoadingEvents(false);
                }
            }
        }

        loadEvents();

        return () => {
            cancelled = true;
        };
    }, []);

    // Adjust this to your own status logic if needed
    const visibleEvents = events
  .filter((e) => e.status !== "draft" && e.status !== "archived")
  .sort((a, b) => {
    const aMs = a?.createdAt ? DateTime.fromISO(a.createdAt).toMillis() : 0;
    const bMs = b?.createdAt ? DateTime.fromISO(b.createdAt).toMillis() : 0;
    return bMs - aMs; // newest first
  })
  .slice(0, maxEvents === 0 ? undefined : maxEvents);





    const renderEventCard = (event) => {
        const {
            id,
            _id,
            name,
            excerpt,
            eventImageUrl,
            baseStartUTC,
            timezone,
            cost,
            slug,
            tags,
            location,
        } = event;

        // --- date/time formatting (NEXT occurrence) ---
        let dateText = "TBD";
        let timeText = "TBD";

        const tz = timezone || "America/Toronto";

        let next = null;
        try {
            next = getNextOccurrence(event);
        } catch (err) {
            console.error("Error computing next occurrence:", err);
        }

        // Fallback for one-time events (or if recurrence logic returns null)
        if (!next && baseStartUTC) {
            const startLocal = DateTime.fromISO(baseStartUTC, { zone: "utc" }).setZone(tz);

            // Optional endLocal (not needed for your card display, but kept consistent)
            let endLocal = null;
            if (event.endsUTC) {
                endLocal = DateTime.fromISO(event.endsUTC, { zone: "utc" }).setZone(tz);
            } else if (Number.isFinite(event.durationMinutes)) {
                endLocal = startLocal.plus({ minutes: event.durationMinutes });
            }

            if (startLocal.isValid) {
                next = {
                    startISO: baseStartUTC,
                    startLocal,
                    endLocal,
                    endLocalISO: event.endsUTC || (endLocal?.toUTC().toISO() ?? null),
                };
            }
        }

        if (next?.startLocal?.isValid) {
            // "Monday, December 29, 2025"
            dateText = next.startLocal.toFormat("cccc, LLLL d, yyyy");

            // "8:00 PM EST"
            const tzShort =
                next.startLocal.offsetNameShort || next.startLocal.toFormat("ZZZ");

            timeText = `${next.startLocal.toFormat("h:mm a")}${tzShort ? ` ${tzShort}` : ""}`;
        }


        // --- categories from tags ---
        const categories =
            Array.isArray(tags) && tags.length > 0
                ? tags.map((t) => ({ label: t, color: "#84b5a1" }))
                : [{ label: "Upcoming", color: "#84b5a1" }];

        // --- price text ---
        const priceText = formatEventCost(cost);

        let cta;
        if (slug) {
            cta = {
                label: "View Details",
                href: `/event/${slug}`,
                variant: "primary",
            };
        } else {
            cta = {
                label: "Details Coming Soon",
                variant: "muted",
            };
        }

        return (
            <EventCard
                key={id || _id || slug || name}
                imageSrc={eventImageUrl || ""}
                imageAlt={name || "Event cover"}
                categories={categories}
                title={name || "Untitled Event"}
                description={excerpt || ""}
                dateText={dateText}
                timeText={timeText}
                locationText={location || "Online"}
                priceText={priceText}
                cta={cta}
            />
        );
    };

    return (
        <section className="events-section">
            {loadingEvents && (
                <p className="page-status events-loading">Loading events&hellip;</p>
            )}

            {eventsError && !loadingEvents && (
                <p className="page-status events-error">{eventsError}</p>
            )}

            {!loadingEvents && !eventsError && (
                <div className="cards-grid events-grid-2">
                    {visibleEvents.length === 0 ? (
                        <p className="page-status events-empty">
                            No upcoming events are available right now. Check back soon!
                        </p>
                    ) : (
                        visibleEvents.map(renderEventCard)
                    )}
                </div>
            )}
        </section>
    );
}

export default EventsSection;
